import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { providersAPI, locationAPI } from '../services/api';
import { Provider } from '../types';
import { MapPin, Star, ArrowLeft, Loader, AlertCircle } from 'lucide-react';
import ReviewDisplay from '../components/ReviewDisplay';

const BrowseDriversPage: React.FC = () => {
  const navigate = useNavigate();
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userLocation, setUserLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [locationError, setLocationError] = useState('');

  useEffect(() => {
    requestLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requestLocation = async () => {
    try {
      setLoading(true);
      setLocationError('');
      console.log('🗺️ Requesting user location...');
      
      const location = await locationAPI.getCurrentLocation();
      console.log('✅ Location obtained:', location);
      
      setUserLocation(location);
      await fetchDrivers(location);
    } catch (error: any) {
      console.error('❌ Location error:', error);
      setLocationError('Location access is required to find nearby drivers. Please enable location permissions.');
      setLoading(false);
    }
  };

  const fetchDrivers = async (location: { latitude: number; longitude: number }) => {
    try {
      setLoading(true);
      console.log('🔍 Fetching drivers near:', location);
      
      const response = await providersAPI.getProvidersByCategory('driver', {
        latitude: location.latitude,
        longitude: location.longitude
      });
      
      console.log('📦 BrowseDrivers Response:', response);
      // Response has structure: { success: true, data: Provider[], pagination: {...} }
      const drivers = response.data || [];
      console.log('✅ Drivers found:', drivers.length);
      setProviders(drivers);
      setError('');
    } catch (error: any) {
      console.error('❌ Error fetching drivers:', error);
      setError('Failed to load drivers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectDriver = (provider: Provider) => {
    // Navigate to driver booking page
    const driverId = provider._id || provider.id;
    console.log('Navigating to book driver:', driverId);
    navigate(`/book-driver/${driverId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-12 w-12 animate-spin text-black mx-auto mb-4" />
          <p className="text-gray-300">
            {userLocation ? 'Finding nearby drivers...' : 'Getting your location...'}
          </p>
        </div>
      </div>
    );
  }

  if (locationError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
          <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white text-center mb-4">
            Location Required
          </h2>
          <p className="text-gray-300 text-center mb-6">{locationError}</p>
          <div className="space-y-3">
            <button
              onClick={requestLocation}
              className="w-full px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800"
            >
              Enable Location
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-gray-300 hover:text-white mb-4"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Home
          </button>
          <h1 className="text-3xl font-bold text-white">Available Drivers</h1>
          {userLocation && (
            <p className="text-gray-600 mt-2">
              <MapPin className="inline h-4 w-4 mr-1" />
              Showing drivers near your location
            </p>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Drivers List */}
        {providers.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <MapPin className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No drivers found nearby
            </h3>
            <p className="text-gray-600 mb-4">
              We couldn't find any drivers in your area. Try again later or expand your search.
            </p>
            <button
              onClick={() => fetchDrivers(userLocation!)}
              className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800"
            >
              Refresh Search
            </button>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {providers.map((provider) => (
              <div
                key={provider._id || provider.id}
                className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow p-6 cursor-pointer"
                onClick={() => handleSelectDriver(provider)}
              >
                {/* Driver Avatar */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    {provider.avatar ? (
                      <img
                        src={provider.avatar}
                        alt={provider.name}
                        className="h-12 w-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center">
                        <span className="text-black font-semibold text-lg">
                          {provider.name.charAt(0)}
                        </span>
                      </div>
                    )}
                    <div className="ml-3">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {provider.name}
                      </h3>
                      <div className="flex items-center">
                        <Star className="h-4 w-4 text-yellow-400 fill-current" />
                        <span className="ml-1 text-sm text-gray-600">
                          {provider.rating.toFixed(1)} ({provider.reviewCount} reviews)
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Distance Badge */}
                  {provider.distance !== undefined && (
                    <div className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm font-medium">
                      {typeof provider.distance === 'number' ? provider.distance.toFixed(2) : provider.distance || '0.00'} km
                    </div>
                  )}
                </div>

                {/* Driver Info */}
                {provider.bio && (
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {provider.bio}
                  </p>
                )}

                {/* Experience & Rate */}
                <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                  {provider.experience && (
                    <span>{provider.experience} years exp.</span>
                  )}
                  {provider.hourlyRate && (
                    <span className="font-semibold text-gray-900">
                      ₹{typeof provider.hourlyRate === 'number' ? provider.hourlyRate.toFixed(2) : provider.hourlyRate}/hr
                    </span>
                  )}
                </div>

                {/* Availability */}
                <div className="flex items-center">
                  <div
                    className={`h-2 w-2 rounded-full mr-2 ${
                      provider.isAvailable ? 'bg-green-400' : 'bg-red-400'
                    }`}
                  />
                  <span
                    className={`text-sm ${
                      provider.isAvailable ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {provider.isAvailable ? 'Available Now' : 'Not Available'}
                  </span>
                </div>

                {/* Book Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectDriver(provider);
                  }}
                  disabled={!provider.isAvailable}
                  className="w-full mt-4 px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Book Driver
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BrowseDriversPage;

